import { Check, MessageCircle, Phone, Warehouse, Trees, Droplets, ShowerHead } from "lucide-react";
import { PageHero, usePageMeta } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, waLink } from "@/lib/site";

const WA_JOIN = waLink("Merhaba FX Motor Sporları, garaj üyeliği (aylık 6.000 TL) hakkında bilgi almak ve üye olmak istiyorum.");

const INCLUDED = [
  { icon: Warehouse, t: "Güvenli garaj", d: "Motorun kulüp garajında güvenle saklanır; taşıma derdi biter." },
  { icon: Trees, t: "Parkur kullanımı", d: "Çekmeköy Ormanları'ndaki parkurumuzda sür." },
  { icon: Droplets, t: "Yıkama ve temizlik", d: "Sürüşten sonra çamurla uğraşma; motorun yıkanır, temizlenir." },
  { icon: ShowerHead, t: "Dolap, duş, ortak alan", d: "Kişisel dolabın, duş imkânı ve kulübün ortak alanları." },
];

const STEPS = [
  { t: "Bize yaz", d: "WhatsApp'tan ya da telefonla üyelik talebini ilet." },
  { t: "Motorunu bırak", d: "Motorun garajımıza yerleşsin, dolabın hazır olsun." },
  { t: "Gel, sür, git", d: "Sürüşünü yap, temizliği bize bırak. Bir dahaki sefere yine hazır." },
];

const PriceCard = () => (
  <div data-testid="membership-price-card" className="relative border border-white/15 bg-black p-8 md:p-10">
    <span className="absolute -top-3 left-8 -skew-x-12 bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white"><span className="block skew-x-12">Garaj Üyeliği</span></span>
    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">Aylık ücret</p>
    <p className="mt-2 font-display text-7xl leading-none text-white md:text-8xl" data-testid="membership-price">6.000 TL<span className="ml-2 text-3xl text-neutral-500">/ ay</span></p>
    <ul className="mt-8 space-y-3 border-t border-white/10 pt-8">
      {INCLUDED.map((i) => <li key={i.t} className="flex gap-3 text-neutral-200"><Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" />{i.t}</li>)}
    </ul>
    <div className="mt-10 flex flex-col gap-3">
      <a href={WA_JOIN} target="_blank" rel="noopener noreferrer" data-testid="membership-join-btn" className="flex items-center justify-center gap-3 bg-brand px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand"><MessageCircle className="h-5 w-5" /> Üye olmak istiyorum</a>
      <a href={PHONE_TEL} data-testid="membership-call-btn" className="flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:border-white"><Phone className="h-5 w-5" /> {PHONE_DISPLAY}</a>
    </div>
  </div>
);

export default function Membership() {
  usePageMeta("Garaj & Üyelik", "FX Motor Sporları garaj üyeliği: güvenli garaj, parkur kullanımı, yıkama, dolap ve duş — aylık 6.000 TL. Çekmeköy / Ömerli.");
  return (
    <>
      <PageHero eyebrow="Garaj & Üyelik" title="Motorun bizde, keyfi sende." intro="Taşıma, yıkama, temizlik derdine son. Motorun garajımızda güvenle dursun; sen gel, sür ve git." img="/ig/ig08.jpg" alt="Enduro motorlarıyla dolu FX garajı" crumbs={[{ label: "Üyelik" }]} />

      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 py-24 md:px-8 md:py-32 lg:grid-cols-[1.2fr_1fr]">
        <Reveal>
          <Eyebrow>FX Garajı</Eyebrow>
          <h2 className="font-display text-5xl uppercase leading-none text-white md:text-7xl">Zamanın sürüşe kalsın.</h2>
          <div className="mt-8 space-y-5 text-base leading-relaxed text-neutral-400 md:text-lg">
            <p>Her hafta motoru römorka yüklemek, eve dönünce çamurunu yıkamak, ekipmanı taşımak… Sürüşten çok uğraşa vakit gider. FX garaj üyeliği bu yükü senden alır.</p>
            <p>Motorun Ömerli'deki kulüp garajımızda güvenle saklanır. Geldiğinde dolabından ekipmanını alır, parkura çıkarsın. Sürüş bitince motorunu bize bırakır, duşunu alıp yoluna devam edersin.</p>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-3">
            <img src="/ig/ig14.jpg" alt="FX garajında sıralanmış enduro motorları" loading="lazy" className="aspect-[4/5] w-full border border-white/10 object-cover" />
            <img src="/ig/ig04.jpg" alt="FX kulüp alanı ve garaj" loading="lazy" className="mt-10 aspect-[4/5] w-full border border-white/10 object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:sticky lg:top-28 lg:self-start">
          <PriceCard />
        </Reveal>
      </section>

      <section className="border-y border-white/10 bg-surface">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
          <Reveal className="mb-14">
            <Eyebrow>Üyeliğe dahil</Eyebrow>
            <h2 className="font-display text-5xl uppercase leading-none text-white md:text-6xl">Tek ücret, dört kolaylık.</h2>
          </Reveal>
          <div className="grid grid-cols-1 border-l border-t border-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {INCLUDED.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08} className="border-b border-r border-white/10 p-8">
                <div data-testid={`membership-included-${i}`}>
                  <p.icon className="h-10 w-10 text-brand" strokeWidth={1.5} />
                  <h3 className="mt-8 font-display text-3xl uppercase leading-tight text-white">{p.t}</h3>
                  <p className="mt-3 leading-relaxed text-neutral-400">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8">
        <Reveal className="mb-14">
          <Eyebrow>Nasıl işler?</Eyebrow>
          <h2 className="font-display text-5xl uppercase leading-none text-white md:text-6xl">Üç adımda üye ol.</h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08} className="border border-white/10 p-8">
              <p className="font-display text-7xl leading-none text-brand">0{i + 1}</p>
              <h3 className="mt-6 font-display text-3xl uppercase text-white">{s.t}</h3>
              <p className="mt-3 text-neutral-400">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="membership-cta" className="bg-brand">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 px-5 py-16 md:flex-row md:items-center md:px-8 md:py-20">
          <div>
            <h2 className="font-display text-5xl uppercase leading-none text-white md:text-6xl">Garajda yerini ayırt.</h2>
            <p className="mt-3 max-w-xl text-white/90">Aylık 6.000 TL. Üyelik için WhatsApp'tan yaz ya da ara: {PHONE_DISPLAY}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href={WA_JOIN} target="_blank" rel="noopener noreferrer" data-testid="membership-whatsapp-btn" className="flex items-center justify-center gap-3 bg-black px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black"><MessageCircle className="h-5 w-5" /> WhatsApp'tan yaz</a>
            <a href={PHONE_TEL} data-testid="membership-cta-call-btn" className="flex items-center justify-center gap-3 border border-white px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand"><Phone className="h-5 w-5" /> Ara</a>
          </div>
        </div>
      </section>
    </>
  );
}
